import { forwardRef } from 'react';
import TemplateFrame from './TemplateFrame';
import ClubBrandMark from './ClubBrandMark';
import MultiDayGridTable from './MultiDayGridTable';
import PickleballBall from './PickleballBall';
import PickleballPaddle from './PickleballPaddle';
import { AVAILABILITY_IMAGE_MULTI_DAY } from '../../constants/bookingConst';
import { fitClubNameSize } from '../../utils/availabilitySnapshotUtil';
import '../../css/AvailabilityMultiDay.css';

// Timeslot table sizing (see planGridRows): what the panel leaves for the
// hour rows once the date bars and court names are placed. Hourly rows are
// kept down to 0.8× the guest page's size, merged into ranges below that.
const GRID_SIZING = { bodyHeight: 770, minHourlyUnit: 0.8, maxUnit: 1.4, maxRowHeight: 80, tableWidth: 1752, maxHeaderUnit: 1.2 };

/**
 * The multi-day "Court Availability" image — every date in the dialog's
 * range side by side, a column per court under each date, like a weekly
 * timetable. Built from the same pieces as the single-day images (brand
 * mark, date pill, title, guest-grid table, open-slots / Book Now footer) so
 * it reads as one family with them; only colours and decoration differ
 * between the dark and light variants (AvailabilityMultiDay.css).
 *
 * @param {object} props
 * @param {object} props.snapshot - buildMultiDaySnapshot() output
 * @param {'dark'|'light'} props.variant
 * @param {'ranges'|'hourly'} [props.rowMode] - 'hourly' forces one row per hour
 * @param {object} [props.imageSettings] - Availability images settings (font sizes in px, BOOKED colours)
 * @returns {JSX.Element}
 */
const MultiDayGridLayout = forwardRef(function MultiDayGridLayout({ snapshot, variant, rowMode, imageSettings }, ref) {
  const iconColor = variant === 'dark' ? snapshot.palette.accentOnDark : snapshot.palette.accentOnLight;
  const note = snapshot.hasPromo ? 'Promo rates on select hours' : snapshot.hasRateRange ? 'Peak hours apply' : '';
  // "Weekly court availability" -> "Weekly court" + accented "availability".
  const titleWords = snapshot.title.split(' ');
  const titleAccent = titleWords.pop();

  return (
    <TemplateFrame
      ref={ref}
      width={AVAILABILITY_IMAGE_MULTI_DAY.width}
      height={AVAILABILITY_IMAGE_MULTI_DAY.height}
      className={`amd amd-${variant}`}
      palette={snapshot.palette}
    >
      {/* ---- decoration ---- */}
      <div className="amd-deco-corner" />
      <div className="amd-deco-stripe" />
      <PickleballPaddle width={200} className="amd-paddle" />
      <PickleballBall size={96} className="amd-ball" />

      {/* ---- header ---- */}
      <div className="amd-header">
        <ClubBrandMark
          snapshot={snapshot}
          iconSize={66}
          iconColor={iconColor}
          nameSize={fitClubNameSize(snapshot.clubNameMain, 36, 18)}
          className="amd-mark"
        />
        <div className="amd-date">
          <span className="amd-date-days">{snapshot.weekdayUpper}</span>
          <span className="amd-date-range">{snapshot.dateCompactUpper}</span>
        </div>
      </div>

      <div className="amd-title-row">
        <div className="amd-title">
          <span className="amd-title-dash" />
          <span>{titleWords.join(' ')} <em>{titleAccent}</em></span>
        </div>
        <div className="amd-subtitle">Reserve your court. Play your game.</div>
      </div>

      {/* ---- table ---- */}
      <div className="amd-panel">
        <MultiDayGridTable
          snapshot={snapshot}
          bodyHeight={GRID_SIZING.bodyHeight}
          minHourlyUnit={GRID_SIZING.minHourlyUnit}
          maxUnit={GRID_SIZING.maxUnit}
          maxRowHeight={GRID_SIZING.maxRowHeight}
          tableWidth={GRID_SIZING.tableWidth}
          maxHeaderUnit={GRID_SIZING.maxHeaderUnit}
          rowMode={rowMode}
          imageSettings={imageSettings}
        />
      </div>

      {/* ---- footer ---- */}
      <div className="amd-footer">
        <div className="amd-slots">
          <span className="amd-slots-label">Open slots</span>
          <span className="amd-slots-value">{snapshot.openCount}<small> of </small>{snapshot.totalCount}</span>
          {note && <span className="amd-slots-note">{note}</span>}
        </div>
        <div className="amd-slogan">
          <span className="amd-slogan-club">{snapshot.clubName}</span>
          <span className="amd-slogan-line">More courts. <em>More rallies.</em></span>
        </div>
        <div className="amd-cta">
          <span className="amd-cta-btn">Book now</span>
          {snapshot.siteHost && <span className="amd-cta-host">{snapshot.siteHost}</span>}
        </div>
      </div>
    </TemplateFrame>
  );
});

export default MultiDayGridLayout;
